
import { useState, useEffect, RefObject } from 'react';
import { FormCaptureFields } from '@/components/vsleads/types';

export function useVideoFormTimer(
  videoRef: RefObject<HTMLVideoElement>,
  formConfig?: FormCaptureFields
) {
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Parse MM:SS format to seconds
  const parseTimeToSeconds = (timeStr: string): number => {
    if (!timeStr || !timeStr.includes(':')) return 0;
    const [mins, secs] = timeStr.split(':').map(Number);
    return (mins * 60) + secs;
  };

  // Check local storage on mount to see if form was already submitted
  useEffect(() => {
    if (!formConfig || !formConfig.videoId) return;
    
    const wasSubmitted = localStorage.getItem(`vsleads-form-${formConfig.videoId}-submitted`) === 'true';
    setFormSubmitted(wasSubmitted);
  }, [formConfig]);

  // Monitor video time and show form at specified timestamp
  useEffect(() => {
    if (!videoRef.current || !formConfig || !formConfig.timerEnabled || formSubmitted) return;

    const targetSeconds = parseTimeToSeconds(formConfig.timerTimestamp);
    
    const handleTimeUpdate = () => {
      const video = videoRef.current;
      if (!video) return;
      
      const currentTime = Math.floor(video.currentTime);
      
      // Show form when we reach the specified time
      if (currentTime >= targetSeconds && !showForm && !formSubmitted) {
        video.pause();
        setShowForm(true);
      }
    };

    const video = videoRef.current;
    video.addEventListener('timeupdate', handleTimeUpdate);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoRef, formConfig, showForm, formSubmitted]);

  // Prevent seeking past the form trigger point when form is active
  useEffect(() => {
    if (!videoRef.current || !formConfig || !formConfig.timerEnabled || formSubmitted) return;
    
    const targetSeconds = parseTimeToSeconds(formConfig.timerTimestamp);
    
    const handleSeeking = () => {
      const video = videoRef.current;
      if (!video) return;
      
      // If trying to skip past the form point when form not submitted, force back
      if (!formSubmitted && video.currentTime > targetSeconds) {
        video.currentTime = targetSeconds;
      }
    };

    const video = videoRef.current;
    video.addEventListener('seeking', handleSeeking);
    
    return () => {
      video.removeEventListener('seeking', handleSeeking);
    };
  }, [videoRef, formConfig, formSubmitted]);

  // Function to handle form submission
  const handleFormSubmit = () => {
    if (!videoRef.current) return;
    
    setFormSubmitted(true);
    setShowForm(false);
    
    // Resume video playback
    const video = videoRef.current;
    video.play().catch(err => console.error('Error playing video:', err));
  };

  return {
    showForm,
    formSubmitted,
    handleFormSubmit
  };
}
